import { useEffect, useRef, useState } from "react";

import { VOICE_OTP_CALLER_ID } from "@utils/func/voiceOtp";

interface OtpBoxesProps {
  otp: string[];
  setOtp: (v: string[]) => void;
  disabled?: boolean;
  /**
   * 重置訊號：父層每次「驗證失敗清空重填」時把這個數字 +1，本元件就把游標移回第一格。
   * 輸入格的 ref 封在元件內部，父層碰不到；驗證失敗只清空 otp 而不聚焦的話，
   * 使用者得自己點回第一格才能重打（autoFocus 只在首次掛載生效，不會再觸發）。
   */
  resetSignal?: number;
}

export const OtpBoxes = ({ otp, setOtp, disabled, resetSignal }: OtpBoxesProps) => {
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  useEffect(() => {
    if (resetSignal === undefined || resetSignal === 0) return;
    inputs.current[0]?.focus();
  }, [resetSignal]);
  /** 從第 start 格起依序填入多個數字（簡訊自動填入/貼上整組驗證碼會一次給整串） */
  const fillFrom = (start: number, raw: string) => {
    const digits = raw.replace(/\D/g, "").split("");
    if (digits.length === 0) return;
    const n = [...otp];
    digits.slice(0, otp.length - start).forEach((d, k) => {
      n[start + k] = d;
    });
    setOtp(n);
    const lastFilled = Math.min(start + digits.length, otp.length) - 1;
    inputs.current[Math.min(lastFilled + 1, otp.length - 1)]?.focus();
  };
  const setDigit = (i: number, v: string) => {
    if (disabled) return;
    // 自動填入/貼上：整組驗證碼會塞進單一格 → 分配到各格（不可只取第一碼）
    if (v.replace(/\D/g, "").length > 1) {
      fillFrom(i, v);
      return;
    }
    if (!/^\d?$/.test(v)) return;
    const n = [...otp];
    n[i] = v;
    setOtp(n);
    if (v && i < otp.length - 1) inputs.current[i + 1]?.focus();
  };
  const onKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) inputs.current[i - 1]?.focus();
  };
  return (
    <div className="flex gap-2 justify-between">
      {otp.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            inputs.current[i] = el;
          }}
          value={d}
          inputMode="numeric"
          // 簡訊驗證碼自動填入：只掛第一格（iOS/Android 會把「整組」塞進這一格，
          // 再由 setDigit/fillFrom 分配到各格）。不可設 maxLength=1——會把整組截成單碼。
          autoComplete={i === 0 ? "one-time-code" : "off"}
          name={i === 0 ? "one-time-code" : undefined}
          disabled={disabled}
          onChange={(e) => setDigit(i, e.target.value)}
          onPaste={(e) => {
            e.preventDefault();
            fillFrom(i, e.clipboardData.getData("text"));
          }}
          onKeyDown={(e) => onKey(i, e)}
          autoFocus={i === 0}
          className="w-[46px] h-[54px] text-center text-[22px] font-bold text-[#333] rounded-[12px] outline-none box-border"
          style={{
            border: `1.5px solid ${d ? "#2196f3" : "#E5E7EB"}`,
            background: d ? "#EFF6FF" : "#F8FAFC",
            opacity: disabled ? 0.6 : 1,
          }}
        />
      ))}
    </div>
  );
};

interface ResendProps {
  /** 使用者按下「重新傳送驗證碼」時呼叫，實際打送出 OTP API 由呼叫端負責。 */
  onResend: () => void;
  /**
   * 使用者按下「改用語音驗證」時呼叫（同一組碼改由電話播報）。
   * 省略＝不顯示語音入口。
   *
   * 回傳 Promise 時，本元件會在等待期間顯示「電話撥打中…」並鎖住入口：
   * 後端的語音發送是**同步等 TwSMS 受理**（send_voice.php，timeout 60 秒），
   * 比簡訊慢得多。沒有等待狀態時使用者按下去畫面毫無變化，只會以為壞掉而狂按。
   */
  onVoice?: () => void | Promise<unknown>;
  /**
   * 語音的冷卻到期時間（epoch 秒），與簡訊分開——後端兩個管道各自計冷卻。
   * 共用一個倒數會在簡訊冷卻中把語音一起鎖住，而那正是使用者收不到簡訊、
   * 最需要改用語音的時刻。
   */
  voiceUntil?: number;
  /**
   * 簡訊是否確實寄出。false 時不顯示「重新傳送／倒數」（那是騙人的），
   * 但**語音入口照樣顯示**——簡訊發送失敗正是最需要改用語音的時候，
   * 把整塊藏起來等於把唯一的救援出口一起藏掉。
   */
  sent?: boolean;
  /**
   * 冷卻到期時間（epoch 秒），由後端決定。
   *
   * 原本是元件自己 `useState(60)`——一掛載就從 60 開始數，與後端毫無關係：
   * 重整／返回再進來都會歸零、使用者可立刻再按，送出後才收到 429，
   * 而畫面顯示的秒數跟真正的冷卻對不起來。後端本來就會回 resendAvailableInSeconds，
   * 只是前端沒用。改由呼叫端把「到期時間」傳進來，這裡只負責顯示與倒數。
   *
   * 用「到期時間」而非「剩餘秒數」：跨重新掛載仍算得出正確剩餘，不必額外保存計時器狀態。
   */
  resendUntil?: number;
}

/** 依到期時間算還剩幾秒（向上取整；已到期回 0）。 */
export const secondsUntil = (untilSec: number | undefined, nowMs: number): number => {
  if (!untilSec || !Number.isFinite(untilSec)) return 0;
  const left = Math.ceil(untilSec - nowMs / 1000);
  return left > 0 ? left : 0;
};

export const Resend = ({ onResend, resendUntil, onVoice, voiceUntil, sent = true }: ResendProps) => {
  const [secs, setSecs] = useState(() => secondsUntil(resendUntil, Date.now()));
  const [voiceSecs, setVoiceSecs] = useState(() => secondsUntil(voiceUntil, Date.now()));
  // 語音外撥請求進行中（見 ResendProps.onVoice）。
  const [voiceBusy, setVoiceBusy] = useState(false);
  const handleVoice = async () => {
    if (!onVoice || voiceBusy) return;
    setVoiceBusy(true);
    try {
      await onVoice();
    } finally {
      setVoiceBusy(false);
    }
  };
  // 每秒依「現在時間」重算，而不是自減：分頁被瀏覽器凍結（背景分頁、鎖屏）時
  // setInterval 會被節流，自減會愈算愈慢，倒數比實際冷卻久。
  useEffect(() => {
    const tick = () => {
      setSecs(secondsUntil(resendUntil, Date.now()));
      setVoiceSecs(secondsUntil(voiceUntil, Date.now()));
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [resendUntil, voiceUntil]);
  return (
    <div className="text-center text-[13px] text-[#94A3B8] mt-4">
      {sent && (
        <div>
          {secs > 0 ? (
            `${secs} 秒後可重新傳送`
          ) : (
            <span className="text-[#2196f3] font-semibold cursor-pointer" onClick={onResend}>
              重新傳送驗證碼
            </span>
          )}
        </div>
      )}
      {/* 收不到簡訊的救援管道：改由系統打電話唸驗證碼（市話也收得到）。
          與「重新傳送」同為藍字，但不加粗、字級小一級，並以上方細線分開——
          它是備援出口，不該和主要動作看起來像兩個同等選項。 */}
      {onVoice && (
        <div className={sent ? "mt-3 pt-3 border-t border-[#F1F5F9]" : ""}>
          {voiceBusy ? (
            <span className="text-[#94A3B8]">電話撥打中，來電號碼 {VOICE_OTP_CALLER_ID}</span>
          ) : voiceSecs > 0 ? (
            <span className="text-[#CBD5E1]">語音驗證 {voiceSecs} 秒後可再試</span>
          ) : (
            <span className="text-[#2196f3] cursor-pointer" onClick={() => void handleVoice()}>
              改用語音驗證
            </span>
          )}
        </div>
      )}
    </div>
  );
};

interface OtpContentProps {
  otp: string[];
  setOtp: (v: string[]) => void;
  hideIcon?: boolean;
  /** 使用者按下「重新傳送驗證碼」時呼叫（實際打 API）。 */
  onResend: () => void;
  /** 驗證進行中時鎖住輸入框，避免重複送出。 */
  disabled?: boolean;
  /** 驗證失敗清空重填時 +1 → 游標自動回到第一格（見 OtpBoxes.resetSignal）。 */
  resetSignal?: number;
  /**
   * 簡訊確實寄出去了才顯示重送倒數。省略＝維持舊行為（一掛載就倒數）。
   * 倒數的意思是「你手上那封還有效，先別急著重寄」——發送失敗時顯示它是騙人的，
   * 而且會讓使用者以為要乾等 60 秒。
   */
  sent?: boolean;
  /** 冷卻到期時間（epoch 秒），由後端決定；見 Resend.resendUntil。 */
  resendUntil?: number;
  /** 按下「改用語音驗證」時呼叫；省略＝不顯示語音入口。見 Resend.onVoice（回傳 Promise 會顯示撥打中）。 */
  onVoice?: () => void | Promise<unknown>;
  /** 語音冷卻到期時間（epoch 秒），與簡訊分開；見 Resend.voiceUntil。 */
  voiceUntil?: number;
}

export const OtpContent = ({
  otp,
  setOtp,
  hideIcon,
  onResend,
  disabled,
  resetSignal,
  sent = true,
  resendUntil,
  onVoice,
  voiceUntil,
}: OtpContentProps) => (
  <>
    {!hideIcon && (
      <div className="w-16 h-16 rounded-full bg-[#EFF6FF] flex items-center justify-center mx-auto mt-1.5 mb-[22px]">
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="5" width="18" height="14" rx="2.5" stroke="#2196f3" strokeWidth="1.8" />
          <path d="M4 8l8 5 8-5" stroke="#2196f3" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    )}
    <OtpBoxes otp={otp} setOtp={setOtp} disabled={disabled} resetSignal={resetSignal} />
    {/* sent=false（簡訊沒寄成功）時仍要渲染：Resend 內部只藏掉「重新傳送」那行，
        語音入口保留——發送失敗正是最需要改用語音的時候。 */}
    <Resend onResend={onResend} resendUntil={resendUntil} onVoice={onVoice} voiceUntil={voiceUntil} sent={sent} />
  </>
);
